import { useState } from 'react';
import { useEffectDelay } from '../useEffectDelay';

/**
 * Options for configuring the useStreak hook.
 * @typedef {Object} UseStreakOptions
 * @property {string} apiBaseUrl - The base URL of the API for fetching streak data.
 * @property {string} gameKey - The unique key identifying the game.
 * @property {string} level - The game level for which the streak data is retrieved.
 * @property {unknown[]} [deps] - Optional dependencies that trigger the API request when changed.
 */
interface UseStreakOptions {
  apiBaseUrl: string;
  gameKey: string;
  level: string;
  deps: unknown[];
}

/**
 * Represents a user's streak in a game.
 * @typedef {Object} Streak
 * @property {number} id - The unique identifier of the streak record.
 * @property {number} user_id - The ID of the user.
 * @property {string} game_key - The key of the game to which this streak belongs.
 * @property {string} level - The game level of this streak record.
 * @property {number} current_play_streak - The user's current play streak.
 * @property {number} current_win_streak - The user's current win streak.
 * @property {number} best_play_streak - The user's best play streak.
 * @property {number} best_win_streak - The user's best win streak.
 * @property {string} last_played_date - The date the user last played.
 * @property {string} created_at - The creation timestamp of the streak record.
 * @property {string} updated_at - The last update timestamp of the streak record.
 */
export type Streak = {
  id: number;
  user_id: number;
  game_key: string;
  level: string;
  current_play_streak: number;
  current_win_streak: number;
  best_play_streak: number;
  best_win_streak: number;
  last_played_date: string;
  created_at: string;
  updated_at: string;
};

/**
 * Custom hook to retrieve a user's game streak from an API.
 * Fetches streak data based on the provided game key and level,
 * and re-fetches when any dependencies in `deps` change.
 *
 * @param {UseStreakOptions} options - Configuration options for fetching streak data.
 * @param {string} options.apiBaseUrl - The base URL of the API.
 * @param {string} options.gameKey - The game key for the streak data.
 * @param {string} options.level - The game level for the streak data.
 * @param {unknown[]} [options.deps=[]] - Optional dependencies to re-trigger the fetch.
 *
 * @returns {{ loading: boolean, streaks: Streak[], error: string | null }} The loading state, streak data, and any error message.
 */
export const useStreak = ({
  apiBaseUrl = 'https://play.hey.gg',
  gameKey,
  level,
  deps,
}: UseStreakOptions) => {
  const [loading, setLoading] = useState(false);
  const [streaks, setStreaks] = useState<Streak | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffectDelay(
    () => {
      setLoading(true);
      setStreaks(null);
      setError(null);

      const accessToken = localStorage.getItem('heygg_jwt');

      // If there's no access token, don't make the fetch call
      if (!accessToken) {
        setLoading(false);
        return;
      }

      fetch(`${apiBaseUrl}/api/streaks?game_key=${gameKey}&level=${level}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      })
        .then((response) => response.json())
        .then((data) => {
          setStreaks(data[data.length - 1]);
        })
        .catch((error) => {
          setError((error as Error).message);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    deps,
    3000,
  );

  return { loading, streaks, error };
};
